import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faUpload,
  faSpinner,
  faArrowLeft,
  faArrowRight,
  faCheckCircle,
  faExclamationTriangle,
  faFileImport,
  faFilter,
  faCheck,
  faTimes,
  faInfoCircle,
  faCrosshairs,
  faListCheck
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import siemImportService from '../services/siemImportService';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const SIEMAlertImport = () => {
  const { t } = useLanguage();
  const { darkMode: isDarkMode } = useTheme();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(1); // 1=upload, 2=preview, 3=confirm
  const [loading, setLoading] = useState(false);
  const [format, setFormat] = useState('auto');
  const [file, setFile] = useState(null);
  const [analyst, setAnalyst] = useState(user?.displayName || user?.username || '');
  const [preview, setPreview] = useState(null);
  const [selectedIndices, setSelectedIndices] = useState(new Set());
  const [severityFilter, setSeverityFilter] = useState('all');
  const [importResult, setImportResult] = useState(null);
  const [autoCorrelate, setAutoCorrelate] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [showTargetMatchOnly, setShowTargetMatchOnly] = useState(false);
  const [minRelevance, setMinRelevance] = useState(0);

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const result = await siemImportService.uploadAlerts(projectId, file, format);
      setPreview(result);
      // Select all non-duplicate items by default
      const indices = new Set(result.items.filter(i => !i.isDuplicate).map(i => i.index));
      setSelectedIndices(indices);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (selectedIndices.size === 0) return;
    setLoading(true);
    try {
      const selectedAlerts = preview.items.filter(i => selectedIndices.has(i.index));
      const result = await siemImportService.confirmImport(projectId, {
        selectedAlerts,
        analyst,
        autoCorrelate
      });
      setImportResult(result);
      setStep(3);
      toast.success(`${result.count} findings imported`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = preview?.items?.filter(i => {
    if (severityFilter !== 'all' && i.severity !== severityFilter) return false;
    if (showTargetMatchOnly && !i.targetMatch) return false;
    if (minRelevance > 0 && (i.relevanceScore || 0) < minRelevance) return false;
    return true;
  }) || [];

  const toggleAll = () => {
    const available = filteredItems.filter(i => !i.isDuplicate).map(i => i.index);
    if (available.every(idx => selectedIndices.has(idx))) {
      const next = new Set(selectedIndices);
      available.forEach(idx => next.delete(idx));
      setSelectedIndices(next);
    } else {
      const next = new Set(selectedIndices);
      available.forEach(idx => next.add(idx));
      setSelectedIndices(next);
    }
  };

  const toggleItem = (idx) => {
    const next = new Set(selectedIndices);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setSelectedIndices(next);
  };

  const dark = (cls, lightCls) => isDarkMode ? cls : lightCls;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate(`/projects/${projectId}/blue-team`)} className={`text-sm mb-2 flex items-center ${dark('text-gray-400 hover:text-gray-200', 'text-gray-500 hover:text-gray-700')}`}>
            <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
            {t('common.back')}
          </button>
          <h1 className={`text-2xl font-bold flex items-center ${dark('text-white', 'text-gray-900')}`}>
            <FontAwesomeIcon icon={faFileImport} className="mr-3 text-blue-500" />
            {t('siemImport.title') || 'Import SIEM Alerts'}
          </h1>
        </div>
        {/* Step indicator */}
        <div className="flex items-center space-x-2">
          {[1, 2, 3].map(s => (
            <div key={s} className={`flex items-center ${s < 3 ? 'mr-2' : ''}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-blue-600 text-white' : dark('bg-gray-700 text-gray-400', 'bg-gray-200 text-gray-500')
              }`}>{s}</div>
              {s < 3 && <div className={`w-8 h-0.5 mx-1 ${step > s ? 'bg-blue-600' : dark('bg-gray-700', 'bg-gray-300')}`}></div>}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className={`rounded-lg shadow p-6 ${dark('bg-gray-800', 'bg-white')}`}>
          <h2 className={`text-lg font-semibold mb-4 ${dark('text-white', 'text-gray-900')}`}>
            {t('siemImport.uploadStep') || 'Upload Alert File'}
          </h2>

          {/* Important note about filtering */}
          <div className={`mb-4 p-4 rounded-lg border ${dark('bg-amber-900/20 border-amber-700 text-amber-300', 'bg-amber-50 border-amber-200 text-amber-800')}`}>
            <div className="flex items-start">
              <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5 mr-3 text-amber-500 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-semibold mb-1">{t('siemImport.filterNote') || 'Important: Filter your SIEM data before importing'}</p>
                <p>{t('siemImport.filterNoteDesc') || 'Import only alerts that are realistically caused by red team actions. After import, you will review findings on the SIEM Review page where you can confirm or dismiss each alert. Only confirmed alerts will appear in Purple Team correlation.'}</p>
              </div>
            </div>
          </div>

          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-all ${
              dragOver ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' :
              file ? 'border-green-400 bg-green-50 dark:bg-green-900/20' :
              dark('border-gray-600 hover:border-gray-400', 'border-gray-300 hover:border-gray-400')
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('siem-file-input').click()}
          >
            <input id="siem-file-input" type="file" className="hidden" accept=".json,.csv,.ndjson,.jsonl,.txt,.log" onChange={handleFileSelect} />
            <FontAwesomeIcon icon={file ? faCheckCircle : faUpload} className={`text-4xl mb-3 ${file ? 'text-green-500' : dark('text-gray-500', 'text-gray-400')}`} />
            <p className={`font-medium ${dark('text-gray-200', 'text-gray-700')}`}>
              {file ? file.name : (t('siemImport.dropFile') || 'Drop file here or click to browse')}
            </p>
            <p className={`text-sm mt-1 ${dark('text-gray-400', 'text-gray-500')}`}>
              JSON, NDJSON, CSV ({t('siemImport.maxSize') || 'max 100MB'})
            </p>
          </div>

          {/* Format selector */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${dark('text-gray-300', 'text-gray-700')}`}>
                {t('siemImport.format') || 'SIEM Format'}
              </label>
              <select
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                className={`w-full px-3 py-2 rounded-md border ${dark('bg-gray-700 border-gray-600 text-white', 'bg-white border-gray-300 text-gray-900')}`}
              >
                {siemImportService.SUPPORTED_FORMATS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${dark('text-gray-300', 'text-gray-700')}`}>
                {t('blueTeam.analyst') || 'Analyst'}
              </label>
              <input
                type="text"
                value={analyst}
                onChange={(e) => setAnalyst(e.target.value)}
                className={`w-full px-3 py-2 rounded-md border ${dark('bg-gray-700 border-gray-600 text-white', 'bg-white border-gray-300 text-gray-900')}`}
                placeholder={t('siemImport.analystPlaceholder') || 'Your name'}
              />
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={handleUpload}
              disabled={!file || loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" /> : <FontAwesomeIcon icon={faArrowRight} className="mr-2" />}
              {t('siemImport.parsePreview') || 'Parse & Preview'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 2 && preview && (
        <div className={`rounded-lg shadow ${dark('bg-gray-800', 'bg-white')}`}>
          {/* Stats bar */}
          <div className={`p-4 border-b ${dark('border-gray-700', 'border-gray-200')}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4 flex-wrap">
                <span className={`text-sm ${dark('text-gray-300', 'text-gray-700')}`}>
                  {t('siemImport.total') || 'Total'}: <strong>{preview.total}</strong>
                </span>
                <span className="text-sm text-green-600">
                  {t('siemImport.new') || 'New'}: <strong>{preview.parsed}</strong>
                </span>
                {preview.duplicates > 0 && (
                  <span className="text-sm text-yellow-600">
                    {t('siemImport.duplicates') || 'Duplicates'}: <strong>{preview.duplicates}</strong>
                  </span>
                )}
                <span className="text-sm text-teal-600">
                  <FontAwesomeIcon icon={faCrosshairs} className="mr-1" />
                  {t('siemImport.targetMatched') || 'Target Matched'}: <strong>{preview.targetMatched || 0}</strong>
                </span>
                <span className="text-sm text-blue-600">
                  {t('siemImport.selected') || 'Selected'}: <strong>{selectedIndices.size}</strong>
                </span>
                <span className={`text-xs px-2 py-0.5 rounded ${dark('bg-gray-700 text-gray-300', 'bg-gray-100 text-gray-600')}`}>
                  {t('siemImport.detectedAs') || 'Detected'}: {preview.detectedFormat}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className={`flex items-center text-sm cursor-pointer ${dark('text-gray-300', 'text-gray-700')}`}>
                  <input type="checkbox" checked={showTargetMatchOnly} onChange={(e) => setShowTargetMatchOnly(e.target.checked)} className="mr-1" />
                  <FontAwesomeIcon icon={faCrosshairs} className="mr-1 text-teal-500" />
                  {t('siemImport.targetOnly') || 'Target match only'}
                </label>
                <FontAwesomeIcon icon={faFilter} className={`text-sm ${dark('text-gray-400', 'text-gray-500')}`} />
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  className={`text-sm px-2 py-1 rounded border ${dark('bg-gray-700 border-gray-600 text-white', 'bg-white border-gray-300')}`}
                >
                  <option value="all">{t('common.all')} Severity</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                  <option value="informational">Info</option>
                </select>
                <select
                  value={minRelevance}
                  onChange={(e) => setMinRelevance(Number(e.target.value))}
                  className={`text-sm px-2 py-1 rounded border ${dark('bg-gray-700 border-gray-600 text-white', 'bg-white border-gray-300')}`}
                >
                  <option value={0}>{t('siemImport.allRelevance') || 'All relevance'}</option>
                  <option value={20}>&ge; 20%</option>
                  <option value={40}>&ge; 40%</option>
                  <option value={60}>&ge; 60%</option>
                  <option value={80}>&ge; 80%</option>
                </select>
              </div>
            </div>
          </div>

          {/* Alert table */}
          <div className="overflow-x-auto max-h-[50vh] overflow-y-auto">
            <table className={`min-w-full divide-y ${dark('divide-gray-700', 'divide-gray-200')}`}>
              <thead className={`sticky top-0 ${dark('bg-gray-700', 'bg-gray-50')}`}>
                <tr>
                  <th className="px-3 py-2 text-left">
                    <input type="checkbox" onChange={toggleAll} checked={filteredItems.filter(i => !i.isDuplicate).length > 0 && filteredItems.filter(i => !i.isDuplicate).every(i => selectedIndices.has(i.index))} />
                  </th>
                  <th className={`px-3 py-2 text-left text-xs font-medium uppercase ${dark('text-gray-400', 'text-gray-500')}`}>Title</th>
                  <th className={`px-3 py-2 text-left text-xs font-medium uppercase ${dark('text-gray-400', 'text-gray-500')}`}>Severity</th>
                  <th className={`px-3 py-2 text-center text-xs font-medium uppercase ${dark('text-gray-400', 'text-gray-500')}`}>
                    <FontAwesomeIcon icon={faCrosshairs} className="mr-1" />Relevance
                  </th>
                  <th className={`px-3 py-2 text-left text-xs font-medium uppercase ${dark('text-gray-400', 'text-gray-500')}`}>Technique</th>
                  <th className={`px-3 py-2 text-left text-xs font-medium uppercase ${dark('text-gray-400', 'text-gray-500')}`}>Timestamp</th>
                  <th className={`px-3 py-2 text-left text-xs font-medium uppercase ${dark('text-gray-400', 'text-gray-500')}`}>Status</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${dark('divide-gray-700', 'divide-gray-200')}`}>
                {filteredItems.map((item) => (
                  <tr key={item.index} className={`${item.isDuplicate ? 'opacity-50' : ''} ${selectedIndices.has(item.index) ? dark('bg-blue-900/20', 'bg-blue-50') : ''} ${item.targetMatch ? dark('border-l-2 border-l-teal-500', 'border-l-2 border-l-teal-400') : ''}`}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        disabled={item.isDuplicate}
                        checked={selectedIndices.has(item.index)}
                        onChange={() => toggleItem(item.index)}
                      />
                    </td>
                    <td className={`px-3 py-2 text-sm max-w-xs truncate ${dark('text-gray-200', 'text-gray-900')}`}>
                      {item.targetMatch && <FontAwesomeIcon icon={faCrosshairs} className="mr-1 text-teal-500" title="Matches red team target" />}
                      {item.title}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        item.severity === 'critical' ? 'bg-red-100 text-red-800' :
                        item.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                        item.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        item.severity === 'low' ? 'bg-green-100 text-green-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>{item.severity}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${
                        item.relevanceScore >= 60 ? 'bg-teal-100 text-teal-800' :
                        item.relevanceScore >= 30 ? 'bg-yellow-100 text-yellow-800' :
                        dark('bg-gray-700 text-gray-400', 'bg-gray-100 text-gray-500')
                      }`} title={item.relevanceReasons?.join(', ') || ''}>
                        {item.relevanceScore || 0}%
                      </span>
                    </td>
                    <td className={`px-3 py-2 text-xs ${dark('text-gray-400', 'text-gray-500')}`}>{item.techniqueId || '-'}</td>
                    <td className={`px-3 py-2 text-xs ${dark('text-gray-400', 'text-gray-500')}`}>
                      {item.timestamp ? new Date(item.timestamp).toLocaleString() : '-'}
                    </td>
                    <td className="px-3 py-2">
                      {item.isDuplicate ? (
                        <span className="text-xs text-yellow-600 font-medium">{t('siemImport.duplicate') || 'Duplicate'}</span>
                      ) : (
                        <span className="text-xs text-green-600 font-medium">{t('siemImport.new') || 'New'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Actions */}
          <div className={`p-4 border-t flex items-center justify-between ${dark('border-gray-700', 'border-gray-200')}`}>
            <button onClick={() => { setStep(1); setPreview(null); setFile(null); }} className={`px-4 py-2 rounded-lg ${dark('bg-gray-700 text-gray-300 hover:bg-gray-600', 'bg-gray-200 text-gray-700 hover:bg-gray-300')}`}>
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              {t('common.back')}
            </button>
            <div className="flex items-center gap-4">
              <label className={`flex items-center text-sm ${dark('text-gray-300', 'text-gray-700')}`}>
                <input type="checkbox" checked={autoCorrelate} onChange={(e) => setAutoCorrelate(e.target.checked)} className="mr-2" />
                {t('siemImport.autoCorrelate') || 'Auto-correlate after import'}
              </label>
              <button
                onClick={handleConfirm}
                disabled={selectedIndices.size === 0 || loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" /> : <FontAwesomeIcon icon={faCheck} className="mr-2" />}
                {t('siemImport.importSelected') || `Import ${selectedIndices.size} Alerts`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === 3 && importResult && (
        <div className={`rounded-lg shadow p-8 text-center ${dark('bg-gray-800', 'bg-white')}`}>
          <FontAwesomeIcon icon={faCheckCircle} className="text-6xl text-green-500 mb-4" />
          <h2 className={`text-2xl font-bold mb-2 ${dark('text-white', 'text-gray-900')}`}>
            {t('siemImport.importComplete') || 'Import Complete'}
          </h2>
          <p className={`text-lg mb-2 ${dark('text-gray-300', 'text-gray-600')}`}>
            {importResult.count} {t('siemImport.findingsCreated') || 'findings created successfully'}
          </p>
          <p className={`text-sm mb-6 ${dark('text-gray-400', 'text-gray-500')}`}>
            {t('siemImport.reviewReminder') || 'Go to SIEM Review to confirm which alerts were caused by red team actions.'}
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <button
              onClick={() => navigate(`/projects/${projectId}/blue-team/siem-review`)}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              <FontAwesomeIcon icon={faListCheck} className="mr-2" />
              {t('siemImport.goToReview') || 'Review Imported Alerts'}
            </button>
            <button
              onClick={() => navigate(`/projects/${projectId}/blue-team`)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
              {t('siemImport.goToBlueTeam') || 'Go to Blue Team Dashboard'}
            </button>
            <button
              onClick={() => { setStep(1); setPreview(null); setFile(null); setImportResult(null); }}
              className={`px-6 py-2 rounded-lg ${dark('bg-gray-700 text-gray-300 hover:bg-gray-600', 'bg-gray-200 text-gray-700 hover:bg-gray-300')}`}
            >
              <FontAwesomeIcon icon={faUpload} className="mr-2" />
              {t('siemImport.importMore') || 'Import More'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SIEMAlertImport;
